import { useMemo, useState, useEffect } from 'react';
import '../../styles/Header.css';
import { useTheme } from '../../contexts/ThemeContext';
import { getAssetPath, createProductUrl } from '../../utils/paths';
import {
    RiHeart3Line,
    RiSearchLine,
    RiShoppingBag2Line,
    RiUserLine,
    RiMenuLine,
    RiCloseLine,
    RiUserFill,
    RiSparkling2Line,
    RiMoonLine,
    RiSunLine
} from 'react-icons/ri';
import { Tooltip } from 'react-tooltip'
import { Link } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { useAccount } from '../../contexts/AccountContext';
import { useApiCall } from '../../hooks/useApiCall';
import { useCounts } from '../../hooks/useCounts';
import { CATALOG_TYPES, useCatalogType } from '../../contexts/CatalogTypeContext';


function Header({ onClick, style }) {
    const logo = getAssetPath("logos/logo_color.png");
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const { account } = useAccount();
    const navigate = useNavigate();
    const apiCall = useApiCall();
    const { isDark, toggleTheme } = useTheme();
    const { catalogType, setCatalogType } = useCatalogType();

    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [headerNavItems, setHeaderNavItems] = useState([
        { id: 'fallback-about', label: 'About Us', url: '/about-us' },
        { id: 'fallback-contact', label: 'Contact', url: '/contact' },
        { id: 'fallback-projects', label: 'Projects', url: '/projects' }
    ]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    
    // Use the custom hook for counts
    const { cartCount, wishlistCount } = useCounts();


    // Fetch categories on component mount
    useEffect(() => {
        // Create URL-friendly slug from category name
        const createSlug = (name) => {
            return name
                .toLowerCase()
                .replace(/[^a-z0-9\s&]/g, '') // Remove special characters except spaces and &
                .replace(/\s*&\s*/g, '-and-') // Replace & with -and-
                .replace(/\s+/g, '-') // Replace spaces with hyphens
                .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
                .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        };

        // Transform API categories to match component format
        const transformCategories = (apiCategories) => {
            return apiCategories.map(category => ({
                id: category.id,
                name: category.name,
                slug: createSlug(category.name),
                items: category.children ? transformCategories(category.children) : undefined
            }));
        };

        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await apiCall('categories', 'POST', { type: catalogType }, null, null);
                
                if (response.success) {
                    // Transform the API response to match the expected format
                    const transformedCategories = transformCategories(response.data.categories);
                    setCategories(transformedCategories);
                } else {
                    console.error('Failed to fetch categories:', response.message);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, [catalogType]);

    // Fetch editable header navigation order
    useEffect(() => {
        apiCall('navigation/header', 'POST', {})
            .then((response) => {
                if (response && response.success && response.data?.items?.length > 0) {
                    setHeaderNavItems(response.data.items);
                }
            })
            .catch((err) => console.error('Error fetching header navigation:', err));
    }, []);

    // Search handler with debouncing
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                setSearchLoading(true);
                apiCall('products/search', 'POST', { 
                    query: searchQuery,
                    limit: 8,
                    type: catalogType
                }).then((response) => {
                    if (response && response.success) {
                        setSearchResults(response.data.products);
                        setShowSearchResults(true);
                    }
                    setSearchLoading(false);
                });
            } else {
                setSearchResults([]);
                setShowSearchResults(false);
            }
        }, 600); // 300ms debounce

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, catalogType]);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Handle Enter key press to navigate to products search page
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
            navigate(`/products?query=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setShowSearchResults(false);
        }
    };

    // Handle search result click
    const handleSearchResultClick = () => {
        setSearchQuery('');
        setShowSearchResults(false);
    };

    // Close dropdown immediately when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-categories-wrapper')) {
                if (hoverTimeout) clearTimeout(hoverTimeout);
                setHoverTimeout(null);
                setHoveredCategory(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [hoverTimeout]);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-search-wrapper')) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const categoriesView = useMemo( () => {

        const handleCategoryMouseEnter = (categoryId) => {
            // Clear any existing timeout to prevent closing
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
            // Immediately show the new category
            setHoveredCategory(categoryId);
        };

        const handleWrapperMouseLeave = () => {
            // Add a delay before closing the dropdown when leaving the entire wrapper
            const timeout = setTimeout(() => {
                setHoveredCategory(null);
            }, 600); // 600ms delay
            setHoverTimeout(timeout);
        };

        const handleWrapperMouseEnter = () => {
            // Clear timeout when re-entering the wrapper
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
        };

        return (
            <div 
                className="header-categories-wrapper"
                onMouseLeave={handleWrapperMouseLeave}
                onMouseEnter={handleWrapperMouseEnter}
            >
                {/* Desktop Categories */}
                <div className={`header-categories ${mobileMenuOpen ? 'mobile-hidden' : ''}`}>
                    {categoriesLoading ? (
                        <div className="categories-loading">Loading categories...</div>
                    ) : categories.length > 0 ? (
                        categories.map( (category) => (
                        <Link
                            to={`/products/category/${category.id}/${category.slug}`}
                            key={category.id} 
                            className={`header-category ${hoveredCategory === category.id ? 'active' : ''}`}
                            onMouseEnter={() => handleCategoryMouseEnter(category.id)}
                        >
                            { category.name }
                        </Link>
                        ))
                    ) : (
                        <div className="categories-empty">No categories available</div>
                    )}
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <>
                        <div 
                            className="header-mobile-overlay"
                            onClick={() => setMobileMenuOpen(false)}
                        />
                        <div className="header-mobile-menu">
                            <div className="header-mobile-menu-content">
                            {categories.map((category) => (
                                <div key={category.id} className="mobile-category-section">
                                    <Link 
                                        className="mobile-category-title"
                                        to={`/products/category/${category.id}/${category.slug}`}
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                        }}
                                    >
                                        {category.name}
                                    </Link>
                                    {category.items && (
                                        <div className="mobile-subcategories">
                                            {category.items.map((subCategory) => (
                                                <div key={subCategory.id} className="mobile-subcategory">
                                                    <Link 
                                                        className="mobile-subcategory-title"
                                                        to={`/products/category/${subCategory.id}/${subCategory.slug}`}
                                                        onClick={() => {
                                                            setMobileMenuOpen(false);
                                                        }}
                                                    >
                                                        {subCategory.name}
                                                    </Link>
                                                    {subCategory.items && (
                                                        <div className="mobile-sub-items">
                                                            {subCategory.items.map((item) => (
                                                                <Link
                                                                    key={item.id}
                                                                    className="mobile-sub-item"
                                                                    to={`/products/category/${item.id}/${item.slug}`}
                                                                    onClick={() => {
                                                                        setMobileMenuOpen(false);
                                                                    }}
                                                                >
                                                                    {item.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    </>
                )}
                
                {/* Dropdown for subcategories */}
                {hoveredCategory && (
                    <div 
                        className="header-dropdown"
                        onClick={() => {
                            if (hoverTimeout) clearTimeout(hoverTimeout);
                            setHoverTimeout(null);
                            setHoveredCategory(null);
                        }}
                    >
                        <div className="header-dropdown-content">
                            {(() => {
                                const activeCategory = categories.find(cat => cat.id === hoveredCategory);
                                if (!activeCategory || !activeCategory.items) return null;
                                
                                return activeCategory.items.map((subCategory) => (
                                    <div key={subCategory.id} className="header-dropdown-column">
                                        <Link to={`/products/category/${subCategory.id}/${subCategory.slug}`} className="header-dropdown-title">
                                            {subCategory.name}
                                            <span className="header-dropdown-arrow">→</span>
                                        </Link>
                                        {subCategory.items && (
                                            <div className="header-dropdown-items">
                                                {subCategory.items.map((subItem) => (
                                                    <Link 
                                                        key={subItem.id} 
                                                        className="header-dropdown-item"
                                                        to={`/products/category/${subItem.id}/${subItem.slug}`}
                                                    >
                                                        {subItem.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                )}
            </div>
        )

    }, [categories, hoveredCategory, mobileMenuOpen, categoriesLoading, hoverTimeout, setHoverTimeout])

  return (
    <div className="header-wrapper" style={style}>
        <Tooltip id="header-tooltip" />

        <div className="header-sub">
            <div className="header-sub-content">
                <div className="catalog-type-tabs" aria-label="Catalog department">
                    {CATALOG_TYPES.map((type) => (
                        <button
                            key={type.id}
                            type="button"
                            className={`catalog-type-tab ${catalogType === type.id ? 'active' : ''}`}
                            onClick={() => {
                                setCatalogType(type.id);
                                navigate('/');
                            }}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
                
                {headerNavItems.map((item) => (
                    <Link key={item.id} to={item.url} className="header-sub-link">
                        {item.label}
                    </Link>
                ))}
            </div>

        </div>
        <div className="header-sticky">
            <div className="header">

                <Link to="/" className="header-logo">
                    <img src={logo} alt={'BDS logo'} title="Building Depot: Lights and Living" />
                </Link>

                <div className="header-search-wrapper">

                    <div className="header-search">
                        <button className="header-search-button" title="Search">
                            <RiSearchLine size={18} />
                        </button>
                        <input 
                            type="text" 
                            className="header-search-input" 
                            placeholder="Search products, categories..." 
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                        />
                    </div>

                    {/* Search Results Dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="header-search-results">
                            {searchResults.map((product) => (
                                <Link
                                    key={product.id}
                                    className="header-search-result-item"
                                    to={createProductUrl(product.id, product.name)}
                                    onClick={handleSearchResultClick}
                                >
                                    {product.image ? (
                                        <img 
                                            src={product.image} 
                                            alt={product.name}
                                            className="search-result-image"
                                        />
                                    ) : (
                                        <div className="search-result-image search-result-image-empty">No Image</div>
                                    )}
                                    <div className="search-result-details">
                                        <div className="search-result-name">{product.name}</div>
                                        {product.brand && (
                                            <div className="search-result-brand">{product.brand}</div>
                                        )}
                                        <div className="search-result-price">
                                            {product.sale_price > 0 ? (
                                                <>
                                                    <span className="price-sale">{product.price.curr} {parseFloat(product.sale_price).toFixed(2)}</span>
                                                    <span className="price-original">{product.price.curr} {parseFloat(product.price.amount).toFixed(2)}</span>
                                                </>
                                            ) : (
                                                <span className="price">{product.price.curr} {parseFloat(product.price.amount).toFixed(2)}</span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {showSearchResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchLoading && (
                        <div className="header-search-results">
                            <div className="header-search-no-results">
                                No products found for "{searchQuery}"
                            </div>
                        </div>
                    )}

                </div>
                    
                { (account?.admin) &&
                <Link to="/experience" className="header-showroom-btn" title="Virtual Showroom" data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="Enter the Virtual Showroom">
                    <RiSparkling2Line size={16} />
                    Showroom
                </Link>
                }
                
                <div className="header-account">
                    {/* <button onClick={toggleTheme} className="dark-mode-toggle" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content={isDark ? 'Light mode' : 'Dark mode'}>
                        {isDark ? <RiSunLine size={20} /> : <RiMoonLine size={20} />}
                    </button> */}
                    <Link to={ account ? '/account' : '/sign-in'} className="header-account-button" title={ account ? 'Account' : 'Sign in'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content={ account ? "Manage your account" : "Sign in to your account"}>
                        { account ?
                        <div className='header-account-avatar' style={{backgroundImage: `url(${account.avatar || getAssetPath('default_avatar.png')})`}}></div>
                        :
                        <RiUserLine size={20} />
                        }
                    </Link>
                    <Link to='/wishlist' className="header-account-button" title={'Wishlist'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your wishlist" style={{ position: 'relative' }}>
                        <RiHeart3Line size={20} />
                        {wishlistCount > 0 && (
                            <span className="header-badge">{wishlistCount}</span>
                        )}
                    </Link>
                    <Link to='/cart' className="header-account-button" title={'Shopping cart'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your shopping cart" style={{ position: 'relative' }}>
                        <RiShoppingBag2Line size={20} />
                        {cartCount > 0 && (
                            <span className="header-badge">{cartCount}</span>
                        )}
                    </Link>
                    
                    {/* Mobile menu toggle - rightmost position */}
                    <button 
                        className="header-mobile-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        title="Menu"
                    >
                        {mobileMenuOpen ? <RiCloseLine /> : <RiMenuLine />}
                    </button>
                </div>
            
            </div>

            { categoriesView }
        </div>
    </div>
  );
}

export default Header;
